/* eslint-disable @typescript-eslint/ban-ts-comment */
/**
 * The external dependencies
 */
import React, { useEffect } from 'react';
import {
  CreateWorkServiceDto,
  GetChangeDto,
  ProcessListForChangeDto,
  ListWorkServiceDto,
  CreatorRole,
  UpdateChangeDtoStatusEnum,
} from '@codefluegel/zeta-change-typescript-client';
import {
  Autocomplete,
  Box,
  Button,
  css,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useWorkServiceApiFactory } from '../../ApiClient';
import { getDepartmentById } from '../../utils/utils';
import { useProjectStore } from '../../store/projectStore';
import usePspLogic from '../../hooks/usePspLogic';
import { NotificationType, NotificationTypes } from '../../types/notification';
import NotificationMessages from '../../constants/notification';
import {
  checkServiceMaterialAccess,
  isChangeManager,
  isKTM,
} from '../../utils/permissionHelper';

export type ServiceModalProps = {
  isOpen: boolean;
  handleModal: () => void;
  change: GetChangeDto;
  currentProcess: ProcessListForChangeDto;
  pushNewServicePackage: (newServicePackage: ListWorkServiceDto) => void;
  replaceUpdatedServicePackage: (servicePackage: ListWorkServiceDto) => void;
  removeServicePackage: (servicePackageId: number) => void;
  selectedService?: ListWorkServiceDto;
  creatorRole: CreatorRole;
  setNotification: (data: NotificationType) => void;
};

const ServiceModal = ({
  isOpen,
  handleModal,
  change,
  currentProcess,
  pushNewServicePackage,
  replaceUpdatedServicePackage,
  selectedService,
  setNotification,
  removeServicePackage,
  creatorRole,
}: ServiceModalProps) => {
  const { t } = useTranslation();
  const { pspPhases } = useProjectStore();

  const pspConfig = {
    phaseIdKey: 'phaseId',
    areaIdKey: 'areaId',
    workPackageIdKey: 'workId',
  };

  const {
    pspAreas,
    handleChangePhasePsp,
    handleChangeAreaPsp,
    pspWorkPackages,
  } = usePspLogic(selectedService, pspPhases, pspConfig);

  const workServiceApi = useWorkServiceApiFactory();
  const NotificationMessage = NotificationMessages();

  const isKTMorCM =
    isKTM(change, Number(currentProcess?.pId)) || isChangeManager(change);

  const roleAccess = checkServiceMaterialAccess(
    change,
    selectedService?.creatorRole,
  );

  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid },
    watch,
  } = useForm<ListWorkServiceDto>({
    mode: 'onChange',
  });

  useEffect(() => {
    if (selectedService) {
      reset({
        wg: selectedService.wg,
        supplier: selectedService.supplier || '',
        price: selectedService.price || 0,
        description: selectedService.description || '',
        areaId: selectedService.areaId || undefined,
        phaseId: selectedService.phaseId || undefined,
        apId: selectedService.apId || undefined,
      });
    }
  }, [selectedService, reset, isOpen]);

  const selectedWg = watch('wg');

  const serviceData = [
    t('service.data.WG07X'),
    t('service.data.WG07'),
    t('service.data.WG08'),
  ];

  const deleteWorkService = async () => {
    try {
      if (selectedService) {
        await workServiceApi.workServicesControllerRemove({
          id: selectedService.id,
        });

        removeServicePackage(selectedService.id);
        setNotification({
          type: NotificationTypes.success,
          message: NotificationMessage.workPackageCreate,
        });
      }

      handleModal();
    } catch (e: any) {
      setNotification({
        type: NotificationTypes.error,
        message: e.response.data.message || NotificationMessage.error,
      });
    }
  };

  const onSubmit = async (data: ListWorkServiceDto) => {
    if (!data.wg) return;

    const groupNo = getDepartmentById(
      Number(currentProcess?.pId ?? 0),
      change?.commonProject?.navProcessToCommonProjectRelations,
    )?.groupNo;

    const payload: CreateWorkServiceDto = {
      position: 0,
      supplier: data.supplier,
      wg: data.wg,
      apId: Number(data.apId) || 0,
      phaseId: Number(data.phaseId) || 0,
      areaId: data.areaId ? Number(data.areaId) : 0,
      price: data.price ? Number(data.price) : null,
      processId: currentProcess.id,
      title: data.title,
      description: data.description,
      groupNo: groupNo || 0,
      creatorRole,
    };

    try {
      if (selectedService) {
        const response = await workServiceApi.workServicesControllerUpdate({
          id: selectedService.id,
          updateWorkServiceDto: payload,
        });
        if (response?.data) {
          replaceUpdatedServicePackage(response.data);
        }
      } else {
        const response = await workServiceApi.workServicesControllerCreate({
          createWorkServiceDto: payload,
        });
        if (response?.data) {
          pushNewServicePackage(response.data);
        }
      }
      handleModal();
    } catch (error) {
      console.error('Failed to save work service', error);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleModal} fullWidth maxWidth="sm">
      <DialogTitle>
        {selectedService ? t('service.edit') : t('service.new')}
        {selectedWg && ` - ${selectedWg}`}
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)} id="service-form">
          <Controller
            name="wg"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                options={serviceData}
                getOptionLabel={option => option}
                value={value}
                onChange={(event, item) => {
                  onChange(item);
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="standard"
                    label={t('service.service')}
                    required
                    sx={{ mt: 2 }}
                  />
                )}
              />
            )}
          />
          {/* Conditionally render AP field if WG07 is selected */}
          {selectedWg && selectedWg.includes('WG07 -') && (
            <Box>
              <Controller
                control={control}
                name="phaseId"
                rules={{ required: true }}
                render={({ field }) => {
                  const curPhase = pspPhases.find(
                    phase => Number(phase.id) === Number(field.value),
                  );
                  return (
                    <Autocomplete
                      popupIcon={
                        <ExpandMoreIcon
                          color="primary"
                          sx={{ color: '#80acd4' }}
                        />
                      }
                      sx={{
                        '& .MuiInput-underline:before': {
                          borderBottom: '1px solid #ececec',
                        },
                        '& .MuiInputLabel-asterisk': {
                          color: '#ff0000',
                        },
                      }}
                      options={pspPhases}
                      value={curPhase || null}
                      getOptionLabel={option =>
                        typeof option === 'string'
                          ? option
                          : `${option.id} ${option.name}`
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      onChange={(e, val) => {
                        if (val) {
                          handleChangePhasePsp(val);
                          field.onChange(val.id);
                        }
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={!field.value}
                          required
                          label={t('service.phasePSP')}
                          variant="standard"
                          sx={{ mt: 2 }}
                        />
                      )}
                    />
                  );
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  marginBottom: 0.5,
                  marginTop: 1,
                }}
              >
                <Controller
                  name="areaId"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Autocomplete
                      options={pspAreas.map(num =>
                        String(num).padStart(2, '0'),
                      )}
                      value={
                        field.value ? String(field.value).padStart(2, '0') : ''
                      }
                      onChange={(e, val) => {
                        if (val) {
                          field.onChange(val);
                          handleChangeAreaPsp(val);
                        }
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          fullWidth
                          variant="standard"
                          label={t('service.area')}
                          required
                          sx={{ mt: 2 }}
                        />
                      )}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="apId"
                  rules={{ required: true }}
                  render={({ field }) => {
                    const selectedWorkPackage = pspWorkPackages.find(
                      phase => Number(phase.id) === field.value,
                    );
                    return (
                      <Autocomplete
                        popupIcon={
                          <ExpandMoreIcon
                            color="primary"
                            sx={{ color: '#80acd4' }}
                          />
                        }
                        sx={{
                          width: '100%',
                          '& .MuiInput-underline:before': {
                            borderBottom: '1px solid #ececec',
                          },
                          '& .MuiInputLabel-asterisk': {
                            color: '#ff0000',
                          },
                        }}
                        getOptionLabel={option =>
                          typeof option === 'string'
                            ? option
                            : `${option.apId} ${option.name}`
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        value={selectedWorkPackage ?? null}
                        onChange={(_, val) => {
                          field.onChange(val ? Number(val.id) : null);
                        }}
                        options={pspWorkPackages}
                        renderInput={params => (
                          <TextField
                            {...params}
                            error={!field.value}
                            required
                            label={t('service.workPackagePSP')}
                            variant="standard"
                            sx={{ mt: 2 }}
                          />
                        )}
                      />
                    );
                  }}
                />
              </Box>
            </Box>
          )}
          <Controller
            name="supplier"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                variant="standard"
                label={t('service.supplier')}
                required
                sx={{ mt: 2 }}
              />
            )}
          />

          <Controller
            name="price"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                variant="standard"
                label={t('service.price')}
                type="number"
                sx={{ mt: 2 }}
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                variant="standard"
                label={t('service.description')}
                multiline
                rows={4}
                sx={{ mt: 2 }}
                required
              />
            )}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {isKTMorCM && selectedService && (
            <Button
              css={css`
                background: #9b9b9b;
                color: #00223b;

                &:hover {
                  background: #9b9b9b;
                }
              `}
              disabled={
                !isValid ||
                !(
                  change?.status === UpdateChangeDtoStatusEnum.InEvaluation ||
                  change?.status === UpdateChangeDtoStatusEnum.EvalRework
                )
              }
              variant="contained"
              component="span"
              onClick={deleteWorkService}
            >
              {t('buttons.delete')}
            </Button>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleModal} sx={{ mr: 1 }}>
              {t('buttons.close')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              form="service-form"
              disabled={!isValid || !roleAccess}
            >
              {t('buttons.save')}
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
ServiceModal.defaultProps = {
  selectedService: undefined,
};

export default ServiceModal;
